import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import NavSection from 'components/NavSection';
import Scrollbar from 'components/Scrollbar';

const SCROLL_THRESHOLD = 2;
const SCROLL_EDGE_HEIGHT = 40;

SidebarNavRegion.propTypes = {
  footerNavConfig: PropTypes.array,
  isShow: PropTypes.bool,
  mainNavConfig: PropTypes.array.isRequired,
  mainNavSx: PropTypes.object,
};

function getScrollPosition(el) {
  const { scrollTop, scrollHeight, clientHeight } = el;

  return {
    canScrollUp: scrollTop > SCROLL_THRESHOLD,
    canScrollDown: scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD,
  };
}

export default function SidebarNavRegion({ mainNavConfig, footerNavConfig, isShow, mainNavSx }) {
  const theme = useTheme();
  const regionRef = useRef(null);
  const footerRef = useRef(null);
  const contentRef = useRef(null);
  const scrollElementRef = useRef(null);
  const [scrollElement, setScrollElement] = useState(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  scrollElementRef.current = scrollElement;

  const updateScrollState = useCallback(() => {
    const region = regionRef.current;
    const content = contentRef.current;
    const footer = footerRef.current;

    if (region && content) {
      const footerHeight = footer?.offsetHeight || 0;
      const availableHeight = region.clientHeight - footerHeight;
      setHasOverflow(content.scrollHeight - availableHeight > SCROLL_THRESHOLD);
    }

    const el = scrollElementRef.current;
    if (el) {
      const position = getScrollPosition(el);
      setCanScrollUp(position.canScrollUp);
      setCanScrollDown(position.canScrollDown);
    }
  }, []);

  const setScrollRef = useCallback((node) => {
    scrollElementRef.current = node;
    setScrollElement(node);
  }, []);

  useLayoutEffect(() => {
    const resolveScrollElement = () =>
      scrollElementRef.current ||
      regionRef.current?.querySelector('.simplebar-content-wrapper') ||
      regionRef.current?.querySelector('.simplebar-scrollable');

    const el = resolveScrollElement();

    if (!el) {
      const retry = window.setTimeout(() => {
        const resolved = resolveScrollElement();
        if (resolved) {
          scrollElementRef.current = resolved;
          setScrollElement(resolved);
        }
      }, 0);

      return () => window.clearTimeout(retry);
    }

    if (!scrollElement) {
      scrollElementRef.current = el;
      setScrollElement(el);
      return undefined;
    }

    updateScrollState();

    const frame = requestAnimationFrame(updateScrollState);
    const delayed = window.setTimeout(updateScrollState, 150);

    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    const resizeObserver = new ResizeObserver(updateScrollState);
    [regionRef.current, footerRef.current, contentRef.current, el].forEach((node) => {
      if (node) resizeObserver.observe(node);
    });

    const contentEl = el.querySelector('.simplebar-content') || el.firstElementChild;
    if (contentEl) {
      resizeObserver.observe(contentEl);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(delayed);
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [scrollElement, updateScrollState, mainNavConfig]);

  useEffect(() => {
    updateScrollState();
  }, [mainNavConfig, updateScrollState]);

  const fadeColor = theme.palette.background.default;
  const hasFooter = Array.isArray(footerNavConfig) && footerNavConfig.length > 0;

  return (
    <Box
      ref={regionRef}
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: hasOverflow ? 'flex-start' : 'space-between',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flex: hasOverflow ? '1 1 auto' : '0 0 auto',
          minHeight: hasOverflow ? 0 : 'auto',
        }}
      >
        <Scrollbar
          onScroll={updateScrollState}
          scrollableNodeProps={{
            ref: setScrollRef,
            onScroll: updateScrollState,
          }}
          sx={{
            height: hasOverflow ? '100%' : 'auto',
            maxHeight: hasOverflow ? '100%' : 'none',
          }}
        >
          <Box ref={contentRef}>
            <NavSection navConfig={mainNavConfig} isShow={isShow} sx={{ pb: 1, ...mainNavSx }} />
          </Box>
        </Scrollbar>

        {hasOverflow && canScrollUp && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 28,
              pointerEvents: 'none',
              zIndex: 2,
              background: `linear-gradient(to bottom, ${fadeColor} 10%, ${alpha(fadeColor, 0)} 100%)`,
            }}
          />
        )}

        {hasOverflow && canScrollDown && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: SCROLL_EDGE_HEIGHT,
              pointerEvents: 'none',
              zIndex: 2,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              pb: 0.5,
              background: `linear-gradient(to bottom, ${alpha(fadeColor, 0)} 0%, ${alpha(fadeColor, 0.88)} 65%, ${fadeColor} 100%)`,
            }}
          >
            <Icon
              icon={arrowIosDownwardFill}
              width={14}
              height={14}
              style={{
                opacity: 0.45,
                color: theme.palette.text.secondary,
              }}
            />
          </Box>
        )}
      </Box>

      {hasFooter && (
        <Box
          ref={footerRef}
          sx={{
            flexShrink: 0,
            pt: 2,
            pb: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <NavSection navConfig={footerNavConfig} isShow={isShow} />
        </Box>
      )}
    </Box>
  );
}
